// services/api.js

import axios from 'axios';
import toast from 'react-hot-toast';

  const  apiUrl  = process.env.REACT_APP_NODE_API_URL

// Create axios instance
const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// REQUEST INTERCEPTOR
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const session_id = localStorage.getItem('session_id');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (session_id) {
      config.headers['x-session-id'] = session_id;
    }

    return config;
  },
  (error) => Promise.reject(error)
);
 

// RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('session_id');
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    } else if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.');
    } else if (error.response?.status === 500) {
      toast.error('Server error. Please try again later.');
    }
    return Promise.reject(error);
  }
);

// AUTH APIs
export const authAPI = {
  register: (data) => api.post('/register', data),
  login: (data) => api.post('/login', data),
  validateAdminCode: (data) => api.post('/validate-admin-code', data),
  requestOTP: (data) => api.post('/request-otp', data),
  verifyOTP: (data) => api.post('/verify-otp', data),
  resendOTP: (data) => api.post('/resend-otp', data),
  forgotPassword: (data) => api.post('/forgot-password', data),
  resetPassword: (data) => api.post('/reset-password', data),
};

// CHAT APIs
export const chatAPI = {
  sendMessage: (data) => api.post("/chat/message", data),
  getHistory: (limit = 50, offset = 0) =>
    api.get(`/chat/history?limit=${limit}&offset=${offset}`),
  getSessions: () => api.get("/chat/sessions"),
  deleteSession: (sessionId) => api.delete(`/chat/session/${sessionId}`),
  getLimits: () => api.get("/chat/limits"),
  processFile: (formData) => {
    return api.post("/chat/run", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadFile: (formData, onUploadProgress) => {
    return api.post("/chat/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    });
  },
};

// ADMIN APIs

// Service functions
export const chatAnalyticsService = {

  getChatAnalytics: async (timeframe = '7days') => {
      try {
          const response = await apiClient.get('/admin/chat-analytics', {
              params: { timeframe }
          });
          return response.data;
      } catch (error) {
          throw error.response?.data || error.message;
      }
  },
  getUserChatHistory: async (userId, limit = 100) => {
      try {
          const response = await api.get(`/admin/user-chat/${userId}`, {
              params: { limit }
          });
          return response.data;
      } catch (error) {
          throw error.response?.data || error.message;
      }
  },
  exportChatData: async () => {
    
      try {
          const response = await api.get('/admin/export-chat-data', {
              responseType: 'blob'
          });
          return response.data;
      } catch (error) {
          throw error.response?.data || error.message;
      }
  },
  downloadChatData: async (filename = 'chat-data.csv') => {
      try {
          const blob = await chatAnalyticsService.exportChatData();
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          return true;
      } catch (error) {
          throw error;
      }
  }
}
export const adminAPI = {
  // User Management
  getUsers: (status = '', search = '') =>
    api.get(`/admin/users?status=${status}&search=${search}`),
  getPendingUsers: () => api.get('/admin/pending-users'),
  getApprovedUsers: () => api.get('/admin/approved-users'),
  getAdmins: () => api.get('/admin/admins'),
  getStats: () => api.get('/admin/stats'),
  getUserAnalytics: () => api.get('/admin/user-analytics'),
  approveUser: (userId) => api.post(`/admin/approve-user/${userId}`),
  rejectUser: (userId) => api.delete(`/admin/reject-user/${userId}`),
  deleteUser: (userId) => api.delete(`/admin/delete-user/${userId}`),
  reactivateUser: (userId) => api.put(`/admin/reactivate-user/${userId}`),
  updateUserRole: (userId, role) =>
    api.put(`/admin/update-user-role/${userId}`, { role }),
  toggleApproval: (userId) => api.put(`/admin/toggle-approval/${userId}`),
  bulkApprove: (userIds) => api.post('/admin/bulk-approve', { userIds }),
  bulkDelete: (userIds) => api.post('/admin/bulk-delete', { userIds }),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (section, data) => api.put(`/admin/settings/${section}`, data),
  updateLLMSettings: (settings) => api.put('/admin/settings/llm', settings),
  updateGeneralSettings: (settings) => api.put('/admin/settings/general', settings),
  updateNotificationSettings: (settings) => api.put('/admin/settings/notifications', settings),
  updateSecuritySettings: (settings) => api.put('/admin/settings/security', settings),

  // Token Management (for chat_token table)
  getTokenStats: () => api.get('/admin/token-stats'),
  getUserTokens: (userId) => api.get(`/admin/user-tokens/${userId}`),  // <-- ADD THIS
  updateUserTokens: (userId, data) => api.put(`/admin/user-tokens/${userId}`, data),
  addUserTokens: (userId, data) => api.post(`/admin/user-tokens/${userId}/add`, data),
  deductUserTokens: (userId, data) => api.post(`/admin/user-tokens/${userId}/deduct`, data),
  resetUserTokens: (userId) => api.post(`/admin/user-tokens/${userId}/reset`),
  
  // User Limits (legacy - kept for compatibility)
  getUserUsage: () => api.get('/admin/user-usage'),
  updateUserLimits: (userId, limits) => api.put(`/admin/user-limits/${userId}`, limits),
  resetUserDailyCounts: (userId) => api.post(`/admin/reset-daily-counts/${userId}`),
  resetUserMonthlyCounts: (userId) => api.post(`/admin/reset-monthly-counts/${userId}`),

  // Chat Analytics
  getChatAnalytics: (timeframe = '7days') =>
    api.get(`/admin/chat-analytics?timeframe=${timeframe}`),
  getUserChatHistory: (userId, limit = 100) =>
    api.get(`/admin/user-chat/${userId}?limit=${limit}`),
  exportChatData: () => api.get('/admin/export-chat-data', { responseType: 'blob' }),

  // Reports
  getReports: (type, range, start = null, end = null) => {
    let url = `/admin/reports?type=${type}&range=${range}`;
    if (start && end) {
      url += `&start=${start}&end=${end}`;
    }
    return api.get(url);
  },
  exportReport: (type, range, format = 'csv', start = null, end = null) => {
    let url = `/admin/export-chat-data?type=${type}&range=${range}&format=${format}`;
    if (start && end) {
      url += `&start=${start}&end=${end}`;
    }
    return api.get(url, { responseType: 'blob' });
  },
};

// USER APIs (for regular users)
export const userAPI = {
  getUserTokens: () => api.get('/user/tokens'),  // For current logged-in user
  getTokenHistory: () => api.get('/user/token-history'),
};

// EXPERIMENTAL — Utilization Explorer
export const explorerAPI = {
  // Record a user's call + reasoning for a case.
  saveResponse: (data) => api.post('/explorer/response', data),
  // Admin: list all submissions (+ headline stats).
  getResponses: (params = {}) => api.get('/explorer/responses', { params }),
};

// SURVEY — clinician pre-exposure survey (one response per user per survey)
export const surveyAPI = {
  // The current user's saved response (draft or completed), or null.
  getMyResponse: (surveyId) => api.get('/survey/response', { params: { surveyId } }),
  // Autosave answers; `completed: true` submits (each submit keeps a copy).
  // Pass `editing: true` when changing an already-submitted response.
  saveResponse: (data) => api.put('/survey/response', data),
  // Drop unsubmitted edits, restoring the last submitted answers.
  discardEdits: (surveyId) => api.post('/survey/response/discard', { surveyId }),
  // Admin: all responses for a survey (`history: 1` adds every submitted version).
  getResponses: (params = {}) => api.get('/survey/responses', { params }),
};

// HEALTH API
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;